/**
 * useActiveSession Hook
 * Detects active homework OR exercise session from URL
 * Used to persist voice assistant across homework and exercise pages
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const useActiveSession = () => {
  const location = useLocation();
  
  const result = useMemo(() => {
    const path = location.pathname;
    const search = location.search;
    
    // ============================================
    // HOMEWORK SESSION DETECTION
    // ============================================
    let pageContext = 'unknown';
    let sessionType = null;
    let sessionId = null;
    
    // Homework: Detect page context
    if (path.includes('/validation-results/')) {
      pageContext = 'results';
      sessionType = 'homework';
    } else if (path.includes('/homework') && path.includes('/voice-tutor/')) {
      pageContext = 'voice-tutor';
      sessionType = 'homework';
    } else if (path.includes('/review/')) {
      pageContext = 'review';
      sessionType = 'homework';
    } else if (path.includes('/topic-analysis/')) {
      pageContext = 'topic-analysis';
      sessionType = 'homework';
    } else if (path.includes('/homework') && search.includes('sessionId=')) {
      pageContext = 'workspace';
      sessionType = 'homework';
    }
    
    // Homework: Pattern 1 - Query parameter - /homework?sessionId=xxx
    if (search.includes('sessionId=')) {
      const params = new URLSearchParams(search);
      const id = params.get('sessionId');
      if (id) {
        console.log('📍 Homework session detected from query param:', id, '| Context:', pageContext);
        return { sessionType: 'homework', sessionId: id, pageContext };
      }
    }
    
    // Homework: Pattern 2 - /homework/validation-results/:sessionId
    const validationMatch = path.match(/\/homework\/validation-results\/([^/]+)/);
    if (validationMatch) {
      const id = validationMatch[1];
      console.log('📍 Homework session detected from validation-results path:', id, '| Context:', pageContext);
      return { sessionType: 'homework', sessionId: id, pageContext };
    }
    
    // Homework: Pattern 3 - /homework/voice-tutor/:sessionId/:problemId
    const voiceTutorMatch = path.match(/\/homework\/voice-tutor\/([^/]+)/);
    if (voiceTutorMatch) {
      const id = voiceTutorMatch[1];
      console.log('📍 Homework session detected from voice-tutor path:', id, '| Context:', pageContext);
      return { sessionType: 'homework', sessionId: id, pageContext };
    }
    
    // Homework: Pattern 4 - /homework/review/:sessionId
    const reviewMatch = path.match(/\/homework\/review\/([^/]+)/);
    if (reviewMatch) {
      const id = reviewMatch[1];
      console.log('📍 Homework session detected from review path:', id, '| Context:', pageContext);
      return { sessionType: 'homework', sessionId: id, pageContext };
    }
    
    // Homework: Pattern 5 - /homework/topic-analysis/:sessionId
    const topicMatch = path.match(/\/homework\/topic-analysis\/([^/]+)/);
    if (topicMatch) {
      const id = topicMatch[1];
      console.log('📍 Homework session detected from topic-analysis path:', id, '| Context:', pageContext);
      return { sessionType: 'homework', sessionId: id, pageContext };
    }
    
    // ============================================
    // EXERCISE SESSION DETECTION
    // ============================================
    
    // Exercise: ONLY detect exercise voice tutor page
    // Regular exercise pages should use general help, not exercise-specific voice agent
    // The exercise voice agent requires an attemptId, which only exists on the tutor page
    
    // Exercise: Pattern 1 - /exercise/voice-tutor/:attemptId/:partId
    const exerciseVoiceTutorMatch = path.match(/\/exercise\/voice-tutor\/([^/]+)/);
    if (exerciseVoiceTutorMatch) {
      const attemptId = exerciseVoiceTutorMatch[1];
      console.log('📍 Exercise session detected from voice-tutor path:', attemptId);
      return { sessionType: 'exercise', sessionId: attemptId, pageContext: 'exercise-voice-tutor' };
    }
    
    // Note: Regular exercise pages (/exercise/:exerciseId) do NOT have a session
    // They should use the general help voice assistant instead
    
    // No session detected
    console.log('📍 No active session detected for path:', path);
    return null;
  }, [location.pathname, location.search]);
  
  return result;
};

// Backward compatibility export
export const useActiveHomeworkSession = () => {
  const session = useActiveSession();
  if (session?.sessionType === 'homework') {
    return { sessionId: session.sessionId, pageContext: session.pageContext };
  }
  return null;
};

